import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';

/* ================================================================ */
/* Shared FAQ — a dark band, reused across pages (Science, Contact,  */
/* Home). Edit the questions here and every page stays in sync.      */
/* ================================================================ */

type FaqItem = { question: string; answer: string; references: string };

const FAQS: FaqItem[] = [
  {
    question: 'Is it scientifically plausible that cancer can be detected from blood?',
    answer:
      'Yes. The scientific basis is firmly established. As tumors develop, they influence metabolism, immune response, and cellular communication throughout the body changes that are reflected in the broad molecular composition of blood. Proteins, metabolites, lipids, and other biochemical species all shift in ways that carry information about the presence of disease. This is no longer a hypothesis. Decades of research have shown that blood serves as a molecular window into systemic health, and that Raman spectroscopy by producing a label-free biochemical fingerprint of a sample is well suited to detect these changes. Studies have demonstrated the feasibility of discriminating cancer patients from healthy individuals through blood and serum analysis using Raman-based methods, with cancer-associated shifts observed across nucleic acids, amino acids, lipids, and proteins. The challenge today is not whether cancer-related molecular information exists in blood it does. The challenge is measuring it with greater sensitivity, reproducibility, and scalability. That is precisely what PreciQube is building toward.',
    references:
      'Hallmarks of Cancer, Hanahan & Weinberg, Cell (2000, 2011); Liquid Biopsy: Current Status and Future Perspectives, Nature Reviews Cancer; Extracellular Vesicles as Liquid Biopsy Biomarkers, Clinical Chemistry (2024); Cell-Free DNA Fragmentomics in Cancer, Cancer Cell (2025).',
  },
  {
    question: 'Has Raman spectroscopy been used for cancer detection before?',
    answer:
      'Yes, extensively. Raman and SERS-based techniques have been investigated for cancer detection for over two decades, across tissues, blood, serum, plasma, and saliva. Large-scale studies involving over 1,500 early-stage patients have demonstrated that serum SERS combined with machine learning can identify multi-cancer molecular signatures with high sensitivity and specificity. The question is no longer whether Raman spectroscopy can detect cancer-associated signatures the evidence is clear. The challenge now is translating these findings into robust, reproducible, and clinically scalable platforms. That is PreciQube’s focus.',
    references:
      'Potential for Raman Spectroscopy to Provide Cancer Screening Using Peripheral Blood Samples (2009); Multi-Cancer Early Detection Based on Serum SERS and Deep Learning, BMC Medicine (2025).',
  },
  {
    question: 'Is Raman spectroscopy an established technology?',
    answer:
      'Yes, and has been for nearly a century. Since C. V. Raman and K. S. Krishnan described the effect in 1928, it has become a standard analytical tool across pharmaceutical manufacturing, semiconductor characterization, food safety, environmental monitoring, and life sciences. Modern advances SERS, stimulated Raman techniques, AI, and quantum-enhanced measurements extend an already proven platform rather than replace it. PreciQube builds on this foundation while pushing its capabilities into molecular diagnostics.',
    references:
      'Raman & Krishnan, Nature 121, 501–502 (1928); Smith & Dent, Modern Raman Spectroscopy: A Practical Approach.',
  },
  {
    question: 'What does SERS contribute, and what is its main limitation?',
    answer:
      'Surface-Enhanced Raman Spectroscopy (SERS) amplifies Raman signals by several orders of magnitude using engineered metallic nanostructures, making measurements accessible that are impossible with conventional Raman. In that sense, SERS has largely solved the signal-strength problem. The field’s central challenge has now shifted to reproducibility: the nanoscale hotspots that produce the strongest signals are highly sensitive to variations in particle geometry and local environment, introducing measurement variability. For clinical diagnostics, consistent and reproducible performance across thousands of samples matters as much as peak sensitivity. The most valuable biosensing platform is not the one with the largest enhancement factor, but the one capable of reliable, repeatable measurements at scale.',
    references:
      'Moskovits, Reviews of Modern Physics 57, 783–826 (1985); Towards Translation of SERS to Clinical Practice: Progress and Trends; Aiming for Maximized and Reproducible Enhancements in SERS (2023).',
  },
  {
    question: 'Have classical Raman systems reached their limits?',
    answer:
      'Many state-of-the-art Raman and stimulated Raman systems now operate near the fundamental shot-noise limit where technical noise has been largely eliminated and sensitivity is constrained by the quantum statistics of light itself. Further gains through conventional engineering deliver progressively smaller returns and are increasingly limited by practical factors such as laser power, photodamage, and acquisition time. This is not a failure; it is a sign of maturity. Historically, whenever conventional approaches approach their fundamental limits, new paradigms emerge. Quantum-enhanced measurements represent exactly that next step.',
    references:
      'Stimulated Raman Scattering Microscopy: A Review, Photonics (2024); de Andrade et al., Optica 7, 470–475 (2020); Casacio et al., Nature 594, 201–206 (2021).',
  },
  {
    question: 'Is quantum-enhanced Raman spectroscopy real?',
    answer:
      'Yes, and experimentally demonstrated. Multiple independent groups have shown that quantum states of light particularly squeezed light can reduce measurement noise below the classical shot-noise limit and improve Raman sensitivity. These results have been reproduced and published in leading peer-reviewed journals. Demonstrations have progressed from theoretical proposals to experiments on polymer samples and living biological cells. The key question is no longer whether quantum-enhanced Raman spectroscopy works; it is how to translate these capabilities into practical, scalable, and clinically relevant systems. That translation is PreciQube’s mission.',
    references:
      "Michael, Bello, Rosenbluh & Pe'er, npj Quantum Information 5, 81 (2019); de Andrade et al., Optica 7, 470–475 (2020); Casacio et al., Nature 594, 201–206 (2021).",
  },
  {
    question: 'Has quantum-enhanced Raman spectroscopy been demonstrated on blood samples?',
    answer:
      'Not yet. Demonstrations to date have used polymer samples and living biological cells. This reflects the youth of the field rather than any limitation of the underlying physics. Applying these advances to blood-based diagnostics is the natural next stage, and it is where PreciQube is focused.',
    references: 'Michael et al. (2019); de Andrade et al. (2020); Casacio et al. (2021).',
  },
  {
    question: 'How do SERS and quantum optics fit together?',
    answer:
      "They address two fundamentally different aspects of the same problem and are highly complementary. SERS strengthens the signal by amplifying molecular responses through engineered nanostructures. Quantum optics lowers the noise floor by reducing the intrinsic quantum noise of optical measurements. Rather than chasing maximum enhancement through extreme hotspots which often sacrifices reproducibility PreciQube's architecture combines controlled, reproducible SERS substrates with quantum-enhanced stimulated Raman measurements operating below the shot-noise limit. The goal is to optimize the overall signal-to-noise ratio across the entire sensing system, not any single component of it.",
    references:
      'Michael et al. (2019); de Andrade et al. (2020); Casacio et al. (2021); Aiming for Maximized and Reproducible Enhancements in SERS (2023).',
  },
  {
    question: 'Can quantum enhancement overcome every limitation in Raman biosensing?',
    answer:
      "No, and it is not intended to. A successful molecular diagnostics platform requires optimization across sample preparation, surface engineering, fluorescence suppression, signal processing, machine learning, and clinical validation. Quantum-enhanced measurements contribute a specific and important advantage at the physical measurement layer improving signal-to-noise beyond what classical optics allows. PreciQube's approach is to optimize the complete measurement stack, treating quantum optics as one powerful component within a broader platform, not a silver bullet.",
    references: 'Caves, Physical Review D (1981); Michael et al. (2019); Casacio et al. (2021).',
  },
  {
    question: "Is PreciQube's approach scientifically grounded, and what risk does it carry?",
    answer:
      'The scientific foundations are well established. Raman spectroscopy has nearly a century of supporting literature; SERS is a mature and extensively researched field; quantum-enhanced Raman measurements have been experimentally demonstrated by multiple independent groups. The remaining challenges are in engineering, manufacturing, clinical validation, and commercialization not in proving new physics. Significant work remains, and as with any emerging platform company, execution risk is real. But the underlying science is solid, and the opportunity lies in bringing these proven principles together into a scalable clinical system.',
    references:
      'Raman & Krishnan (1928); Moskovits (1985); Michael et al. (2019); de Andrade et al. (2020); Casacio et al. (2021).',
  },
];

const FaqRow: React.FC<{ item: FaqItem; index: number; isOpen: boolean; onToggle: () => void }> = ({
  item,
  index,
  isOpen,
  onToggle,
}) => (
  <div className="border-b border-white/10">
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-start gap-5 py-6 text-left group"
    >
      <span className="text-[#748ffc] font-mono text-sm font-bold pt-1 w-8 flex-shrink-0">
        {String(index + 1).padStart(2, '0')}
      </span>
      <span className="flex-1 text-lg font-semibold text-white/90 group-hover:text-white transition-colors">
        {item.question}
      </span>
      <span
        className={`flex-shrink-0 w-7 h-7 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-transform duration-300 ${
          isOpen ? 'rotate-45' : ''
        }`}
      >
        <Plus className="w-4 h-4" />
      </span>
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="overflow-hidden"
    >
      <div className="pb-7 pr-10 space-y-4" style={{ paddingLeft: '3.25rem' }}>
        <p className="text-white/60 leading-relaxed">{item.answer}</p>
        <p className="text-xs text-white/35 italic leading-relaxed pt-3 border-t border-white/10">
          <span className="text-[#748ffc] not-italic font-semibold">References: </span>
          {item.references}
        </p>
      </div>
    </motion.div>
  </div>
);

/**
 * Shared FAQ band.
 * @param breakout   Tailwind negative-margin classes so the dark band spans the
 *                   full width of its parent's padding (e.g. "-mx-6 md:-mx-12").
 * @param fullBleed  Break out to the full viewport width regardless of the
 *                   parent container's max-width. Use inside centered pages.
 */
const FaqSection: React.FC<{
  breakout?: string;
  fullBleed?: boolean;
  className?: string;
}> = ({ breakout = '', fullBleed = false, className = 'mt-28' }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const bleed = fullBleed ? 'w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]' : breakout;
  return (
    <section
      id="faq"
      className={`scroll-mt-28 bg-[var(--footer-bg)] py-20 md:py-28 transition-theme ${bleed} ${className}`}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-14">
          <span className="text-[#748ffc] font-bold text-xs uppercase tracking-[0.25em]">
            Frequently Asked Questions
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4 text-white leading-tight">
            Is the science actually sound?
          </h2>
          <p className="text-white/50 mt-5 max-w-2xl leading-relaxed">
            Honest answers to the questions investors, scientists, and partners ask most with
            references to the peer-reviewed literature behind each one.
          </p>
        </div>
        <div>
          {FAQS.map((item, i) => (
            <FaqRow
              key={i}
              item={item}
              index={i}
              isOpen={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
